import { FeatureFlag } from '@ps/web-core-enums';
import { type IspSportsAlias } from '@ps/web-core-store';
import { useSelector, useStore } from 'react-redux';

import useFeature from '@swf/core/hooks/useFeature';
import { select } from '@swf/core/store';
import type { LinkItem } from '@swf/generated/types/api/sports';
import { EventTypeId } from '@swf/sports/types/eventType';
import { getEventTypeIcon } from '@swf/sports/utils/getEventTypeIcon';

import { configuredLandingPageEventTypeIds } from '../pages/config';
import type { IconName } from '../types/icon';
import { generateSportsPath } from '../utils/generateSportsPath';
import { isGreyhoundRacing, isHorseRacing } from '../utils/isEventTypeId';

export const defaultToMatchesTab = [
  EventTypeId.SOCCER,
  EventTypeId.TENNIS,
  EventTypeId.BASKETBALL,
  EventTypeId.AMERICAN_FOOTBALL,
  EventTypeId.BASEBALL,
  EventTypeId.ICE_HOCKEY
];

const getRoute = (
  aliases: ReturnType<typeof select.cms.getAliases>,
  { eventTypeId, type }: Pick<LinkItem, 'eventTypeId' | 'type'>,
  isEventTypeLandingPage: boolean
): string => {
  let alias: IspSportsAlias = 'sportsSport';

  switch (type) {
    case 'EVENT':
      alias = 'sportsEvent';
      break;
    case 'COMPETITION':
      alias = 'sportsCompetition';
      break;
    case 'EVENT_TYPE':
    default:
      if (isEventTypeLandingPage) {
        alias = 'sportsLanding';
        break;
      } else if (defaultToMatchesTab.includes(eventTypeId)) {
        alias = 'sportsMatchFiltering';
        break;
      }
  }

  return aliases[alias] ?? '/';
};

export interface MenuItemWithUrlAndIcon {
  icon: IconName;
  text: string;
  url: string;
}

/**
 * Gets the url based on the link type and appends the correct icon for the
 * link.
 */
const useUrlMenuItems = (menuItems?: LinkItem[]): MenuItemWithUrlAndIcon[] => {
  const isHorseRacingEnabled = useFeature(FeatureFlag.HorseRacingEnabled);
  const isGreyhoundsEnabled = useFeature(FeatureFlag.GreyhoundRacingEnabled);
  const aliases = useSelector(select.cms.getAliases);
  const store = useStore();

  return (
    menuItems?.reduce<MenuItemWithUrlAndIcon[]>((acc, { type, text, ...values }) => {
      if (isHorseRacing(values.eventTypeId) && !isHorseRacingEnabled) return acc;
      if (isGreyhoundRacing(values.eventTypeId) && !isGreyhoundsEnabled) return acc;

      const isEventTypeLandingPage = select.sports.isEventTypeLandingPage(store.getState(), {
        eventTypeId: values.eventTypeId,
        configuredLandingPageEventTypeIds
      });

      return [
        ...acc,
        {
          icon: getEventTypeIcon(values.eventTypeId),
          url: generateSportsPath(
            getRoute(aliases, { type, eventTypeId: values.eventTypeId }, isEventTypeLandingPage),
            {
              ...values
            }
          ),
          text
        }
      ];
    }, []) ?? []
  );
};

export default useUrlMenuItems;
